import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicLayout from '@/components/PublicLayout';
import SEO from '@/components/SEO';
import EventCard from '@/components/EventCard';
import { PageLoading } from '@/components/Loading';
import api from '@/lib/api';
import { Globe, Phone, Instagram, MapPin, Building2 } from 'lucide-react';
import { zoneSlug } from '@/lib/seo';

export default function LocaleDetail() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/locali/${slug}`).then((r) => setData(r.data)).catch(() => setError(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (<PublicLayout><PageLoading /></PublicLayout>);
  if (error || !data?.locale) return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-heading text-3xl mb-3">Locale non trovato</h1>
        <Link to="/locali" className="text-[color:var(--gold)]">Tutti i locali</Link>
      </div>
    </PublicLayout>
  );

  const { locale, eventi } = data;
  const baseUrl = typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : '';

  return (
    <PublicLayout>
      <SEO
        title={`${locale.nome} — ${locale.zona}, Roma | Eventi e Serate`}
        description={`${locale.nome} a Roma in zona ${locale.zona}. Scopri prossimi eventi, gallery e info del locale.`}
        canonical={`${baseUrl}/locali/${locale.slug}`}
        image={locale.logo || locale.gallery?.[0]}
      />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 hero-overlay relative">
        <Link to={`/serate-roma-${zoneSlug(locale.zona)}`} className="text-xs uppercase tracking-[0.3em] text-[color:var(--gold)] inline-flex items-center gap-1 relative z-10"><MapPin className="w-3 h-3"/>{locale.zona}, Roma</Link>
        <div className="flex items-end gap-5 mt-4 relative z-10">
          {locale.logo && (
            <div className="w-20 h-20 rounded-xl overflow-hidden border border-[color:var(--border-c)] bg-[color:var(--surface)] flex-shrink-0">
              <img src={locale.logo} alt={locale.nome} className="w-full h-full object-cover"/>
            </div>
          )}
          <h1 data-testid="locale-page-title" className="font-heading text-4xl md:text-5xl">{locale.nome}</h1>
        </div>
        <div className="mt-4 flex flex-wrap gap-3 text-sm text-[color:var(--text-muted)] relative z-10">
          {locale.indirizzo && <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-[color:var(--gold)]"/>{locale.indirizzo}</span>}
          {locale.telefono && <a href={`tel:${locale.telefono}`} className="flex items-center gap-1 hover:text-[color:var(--gold)]"><Phone className="w-4 h-4 text-[color:var(--gold)]"/>{locale.telefono}</a>}
          {locale.sito_web && <a href={locale.sito_web} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-[color:var(--gold)]"><Globe className="w-4 h-4 text-[color:var(--gold)]"/>Sito</a>}
          {locale.instagram && <a href={locale.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-[color:var(--gold)]"><Instagram className="w-4 h-4 text-[color:var(--gold)]"/>Instagram</a>}
        </div>
      </section>

      {locale.gallery?.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-10">
          <h2 className="font-heading text-xl mb-3">{t('locale.gallery')}</h2>
          <div className="flex gap-3 overflow-x-auto no-scrollbar scroll-snap-x pb-2">
            {locale.gallery.map((src, i) => (
              <div key={src} className="snap-start min-w-[80%] sm:min-w-[45%] md:min-w-[32%] aspect-[4/3] rounded-xl overflow-hidden border border-[color:var(--border-c)] bg-[color:var(--surface)]">
                <img src={src} alt={`${locale.nome} ${i+1}`} className="w-full h-full object-cover" loading="lazy"/>
              </div>
            ))}
          </div>
        </section>
      )}

      {locale.descrizione && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-12">
          <h2 className="font-heading text-2xl mb-3">{t('locale.about')}</h2>
          <div className="prose prose-invert max-w-none text-[color:var(--text-muted)] leading-relaxed whitespace-pre-line">{locale.descrizione}</div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-4 sm:px-6">
        <h2 className="font-heading text-2xl md:text-3xl mb-4">{t('locale.upcomingEvents', { name: locale.nome })}</h2>
        {eventi.length === 0 ? (
          <div className="text-center py-12 rounded-2xl border border-[color:var(--border-c)] bg-[color:var(--surface)]">
            <Building2 className="w-10 h-10 mx-auto text-[color:var(--gold)] opacity-40 mb-3"/>
            <p className="text-[color:var(--text-muted)]">{t('empty.events')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {eventi.map((e) => <EventCard key={e.id} evento={e} locale={locale} />)}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
